import { Dropdown, DropdownMenuProps } from '@nextui-org/react';
import styled from 'styled-components';

import { SearchIcon } from '~/assets';
import { DropDownButton, DropDownMenu } from '~/components/Dropdown/Dropdown.styles';
import Input from '~/components/Input';
import { ThemeProp } from '~/theme/Themes';

export const StyledDropdown = styled(Dropdown)`
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const headerVariantCss = (props) => ({
  'padding-left': 0,
  'padding-right': 0,
  fontSize: props.theme.fontSize.ml,
  fontWeight: props.theme.fontWeight.medium,
  // Explicit minWidth (rather than the browser default "auto") lets this flex item shrink
  // below its text's natural width so the header row can still fit the right-side actions;
  // the button's own text span already ellipsizes via .nextui-button-text.
  minWidth: 0,
  // Keep the trigger at full strength while the menu is open (NextUI otherwise dims/scales
  // the button in its expanded state). Overrides the base rule which only unsets transform.
  '&.nextui-dropdown-button[aria-haspopup="true"].nextui-dropdown-button[aria-expanded="true"]': {
    transform: 'unset',
    opacity: 1,
  },
  // Rotate the chevron while the menu is open. Keep the base stroke color — this object
  // key replaces the base rule for `.nextui-button-icon-right`, so re-declare it here.
  '.nextui-button-icon-right': {
    stroke: props.theme.colors.neutrals.black,
    transition: 'transform 0.1s ease-in-out',
  },
  '&.nextui-dropdown-button[aria-expanded="true"] .nextui-button-icon-right': {
    transform: 'rotate(180deg)',
  },
});

export const StyledDropDownButton = styled(DropDownButton).attrs((props) => {
  const isHeaderVariant = props.$variant === 'header';

  return {
    css: {
      ...props.css,
      height: '2rem',
      width: 'fit-content',
      'border-radius': props.shouldApplyRoundCorners ? '2rem' : props.theme.borderRadius,
      ...(isHeaderVariant && headerVariantCss(props)),
    },
  };
})``;

interface StyledDropDownMenuProps extends DropdownMenuProps {
  $maxHeight?: string;
  $variant?: 'header';
}
export const StyledDropDownMenu = styled(DropDownMenu)<{ $variant?: 'header' }>`
  overflow-y: auto;
  max-height: ${(props: StyledDropDownMenuProps) => props.$maxHeight || '20rem'};
  // DropDownMenu's own \`width\` prop (computed from the trigger button's measured width, see
  // useDropdown's buttonWidth) already sizes the menu dynamically; this is only a floor so
  // narrower triggers keep their existing ~400px menu, while a wider trigger (e.g. a full-row
  // audience selector) can grow past it to match.
  min-width: 25rem;

  /* react-aria keeps keyboard focus on the last-hovered item after the pointer leaves, which
     left a stuck grey background. Only show a background while actually hovering (but keep the
     selected item's highlight, and the keyboard-focus highlight via :focus-visible so keyboard
     navigation stays visible). */
  & .nextui-dropdown-item.is-focused:not(:hover):not(.is-selected):not(:focus-visible) {
    background: transparent;
  }

  /* Typing (keyboard) leaves the input modality as "keyboard", so hovering an item right after
     searching still triggers NextUI's :focus-visible ring. Suppress it while also hovered. A
     keyboard-only navigated item (mouse not over it) keeps its ring for accessibility. */
  & .nextui-dropdown-item:focus-visible:hover {
    box-shadow: none;
    outline: none;
  }

  /* Header variant renders custom items with no separators — space them via the container gap,
     and drop the base horizontal item padding. */
  ${(props: StyledDropDownMenuProps) =>
    props.$variant === 'header' &&
    `
      display: flex;
      flex-direction: column;
      gap: 1rem;
    `}

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    min-width: 22rem;
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.smallMobile}) {
    width: 17rem;
  }
`;

export const StyledInput = styled(Input)`
  height: 2rem !important;
  width: calc(100% - 1.5rem);
  margin-bottom: 0 !important;
  margin: 0 0.75rem;
`;

export const StyledSearchIcon = styled(SearchIcon)`
  stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
`;

export const NoResults = styled.span`
  margin: 0.125rem 1rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  display: flex;
  align-items: center;
  height: 2.5rem;
`;

export const HeaderScrollArea = styled.div`
  position: relative;
`;

// White→transparent overlay pinned to the bottom of the scroll list, hinting there are more
// items below the fold. pointer-events: none so it never blocks scrolling or item clicks.
export const BottomFade = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2.5rem;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    ${(props: ThemeProp) => props.theme.colors.neutrals.white}00,
    ${(props: ThemeProp) => props.theme.colors.neutrals.white}
  );
`;
